import { useEffect } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useAccountType, useFreelancerId } from "@js/apps/common/hooks";
import { useGetEmployerPublicProfileQuery } from "@js/apps/employer/api";
import { fetchFreelancerProfile } from "@js/apps/freelancer/actions";
import { useGetFreelancerBidsQuery } from "@js/apps/freelancer/api";
import { useAppDispatch, useAppSelector } from "@js/hooks";

import { useGetJobQuery } from "../../api";

export const useJobDetails = (jobId: number) => {
  const dispatch = useAppDispatch();
  const { isFreelancer } = useAccountType();
  const { data: job, isFetching, isError } = useGetJobQuery({ id: jobId });
  const { data: currentJobBids, isLoading: isLoadingBids } =
    useGetFreelancerBidsQuery({ job: jobId }, { skip: !isFreelancer });

  const jobEmployerId = job?.employer?.id;
  const { data: employerProfile, isFetching: fetchingEmployerPublicProfile } =
    useGetEmployerPublicProfileQuery(jobEmployerId ?? skipToken);

  const freelancerId = useFreelancerId();
  const freelancerProfileId = useAppSelector(
    (state) => state.freelancer.freelancerProfile?.id,
  );

  useEffect(() => {
    if (!jobId || !freelancerId) {
      return;
    }

    const profileIsFetched = !!freelancerProfileId;
    if (profileIsFetched) {
      return;
    }

    void dispatch(fetchFreelancerProfile(freelancerId));
  }, [dispatch, jobId, freelancerId, freelancerProfileId]);

  const bid = currentJobBids?.[0];

  const loading =
    isFetching ||
    !job ||
    !employerProfile ||
    fetchingEmployerPublicProfile ||
    isLoadingBids;

  return {
    loading,
    job,
    bid,
    employerProfile,
    errorStatus: isError,
  };
};
